import { z } from 'zod';
import { DocId, IsoTimestamp } from '../common.js';
import { AgentCitation } from './agent.js';
/**
 * Canvas — persistent multi-turn artifact authoring (issue #3).
 *
 * Firestore paths:
 *   tenants/{tenantId}/users/{userId}/chats/{chatId}/canvas/current
 *   tenants/{tenantId}/users/{userId}/chats/{chatId}/canvas/current/versions/{n}
 *
 * One canvas per chat. The agent rewrites canvas/current whole-document
 * per turn; each turn writes a new versions/{n} row for rollback. The
 * canvas pane is read-only to the user in v1 — direct edit is deferred.
 */
/**
 * Chat-thread transcript line the backend writes as the assistant turn of
 * a canvas turn (the canvas content itself lives in the pane, not the
 * thread). The frontend recognises these lines to avoid offering
 * "Add to canvas" on them — adding the status line to the canvas would be
 * nonsense. Keep the builder and the matcher together so they can't drift.
 */
export function canvasTranscriptMarker(mode, version) {
    return mode === 'create'
        ? `Created the canvas (v${version}). It is open in the Canvas panel on the right.`
        : `Updated the canvas to v${version}. See the Canvas panel on the right.`;
}
// Also matches the pre-GA wording ("Canvas updated to vN.") still stored
// in older chat threads.
const CANVAS_TRANSCRIPT_MARKER_RE = /^(?:Canvas updated to v\d+\.|Created the canvas \(v\d+\)\. It is open in the Canvas panel on the right\.|Updated the canvas to v\d+\. See the Canvas panel on the right\.)$/;
export function isCanvasTranscriptMarker(text) {
    return CANVAS_TRANSCRIPT_MARKER_RE.test(text.trim());
}
export const CanvasStarterTemplate = z.enum([
    'spec',
    'client_presentation',
    'test_cases',
    'marketing_onepager',
    'blank',
]);
/**
 * Canvas mode flag on `/agent/ask`. 'none' = no canvas interaction (default,
 * preserves pre-canvas behaviour). 'create' = first canvas turn on this chat.
 * 'update' = subsequent turn rewrites canvas/current.
 */
export const CanvasMode = z.enum(['create', 'update', 'none']);
/**
 * What kind of edit the user asked for. Determines whether retrieval runs:
 *   structural — rephrase / shorten / reorder. NO retrieval.
 *   content    — add a section / verify / look up. Retrieval RUNS.
 *   mixed      — both. Retrieval runs (treated as content for safety).
 *   initial    — first turn (create) or a restore. No classification.
 */
export const CanvasIntent = z.enum(['structural', 'content', 'mixed', 'initial']);
/**
 * One accumulated citation. Reuses AgentCitation verbatim (so docx/pptx
 * exporters can pass entries through with zero re-mapping) and adds
 * `firstCitedAt` so the canvas-versions trail records when each chunk
 * entered the snapshot.
 */
export const CanvasCitationSnapshotEntry = AgentCitation.extend({
    firstCitedAt: IsoTimestamp,
});
/**
 * `chats/{chatId}/canvas/current` document shape.
 *
 * Size cap is enforced server-side at write time on `markdownContent.length`
 * (UTF-16 code-unit count via JS string length — predictable, no tokenizer
 * dependency). Default cap is CANVAS_MAX_CHARS=32000 (~8000 tokens at the
 * typical 4-chars/token English ratio).
 *
 * `lockedUntil` is the optimistic-lock TTL: set to now+180s when an agent
 * canvas turn starts (turns are non-streaming, so it is never refreshed),
 * cleared on done/error. A second canvas turn or a restore while
 * `lockedUntil > now` returns 409 `canvas_busy`. On an instance crash the
 * TTL expires naturally.
 */
export const ChatCanvas = z.object({
    chatId: DocId,
    markdownContent: z.string(),
    version: z.number().int().nonnegative(),
    updatedAt: IsoTimestamp,
    lockedUntil: IsoTimestamp.nullable(),
    citationSnapshot: z.array(CanvasCitationSnapshotEntry),
    starterTemplate: CanvasStarterTemplate.nullable(),
});
/**
 * `chats/{chatId}/canvas/versions/{n}` document shape — one per turn that
 * landed (validation-rejected turns do NOT write a version). 1-indexed.
 *
 * `fromTurn` is the messageId of the user message that triggered this
 * version, so the UI can deep-link a version back to the chat turn that
 * produced it.
 */
export const ChatCanvasVersion = z.object({
    v: z.number().int().positive(),
    markdownContent: z.string(),
    createdAt: IsoTimestamp,
    fromTurn: DocId,
    intent: CanvasIntent,
    citationSnapshot: z.array(CanvasCitationSnapshotEntry),
});
//# sourceMappingURL=canvas.js.map